import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Contract, InterfaceAbi } from 'ethers';
import { ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { EVMProviderFactoryService } from '../provider/evm-provider-factory.service';
import { EVMBlockChainExplorerFactoryService } from '../wallets/explorers/evm-blockchain-explorer.service';
import { IBlockChainExplorer } from '../wallets/explorers/types';
import { BaseContractFactory } from './contract-factory-base';

export interface IErc20ContractData
{
    contract: Contract;
    symbol?: string;
    name?: string;
    abi?: any;
}

@Injectable({
    providedIn: 'root',
})
export class EVMErc20ContractFactory extends BaseContractFactory
{
    protected data: {
        [ address: string ]: {
            [ network: string ]: ReplaySubject<IErc20ContractData>;
        };
    } = {};

    protected explorer: IBlockChainExplorer;

    protected currenciesData: ReplaySubject<{
        [ address: string ]: { name: string; symbol: string; };
    }> = new ReplaySubject(1);

    protected readonly ERC20_ABI = [
        {
            constant: true,
            inputs: [],
            name: 'name',
            outputs: [
                {
                    name: '',
                    type: 'string',
                },
            ],
            payable: false,
            stateMutability: 'view',
            type: 'function',
        },
        {
            constant: false,
            inputs: [
                {
                    name: '_spender',
                    type: 'address',
                },
                {
                    name: '_value',
                    type: 'uint256',
                },
            ],
            name: 'approve',
            outputs: [
                {
                    name: '',
                    type: 'bool',
                },
            ],
            payable: false,
            stateMutability: 'nonpayable',
            type: 'function',
        },
        {
            constant: true,
            inputs: [],
            name: 'totalSupply',
            outputs: [
                {
                    name: '',
                    type: 'uint256',
                },
            ],
            payable: false,
            stateMutability: 'view',
            type: 'function',
        },
        {
            constant: false,
            inputs: [
                {
                    name: '_from',
                    type: 'address',
                },
                {
                    name: '_to',
                    type: 'address',
                },
                {
                    name: '_value',
                    type: 'uint256',
                },
            ],
            name: 'transferFrom',
            outputs: [
                {
                    name: '',
                    type: 'bool',
                },
            ],
            payable: false,
            stateMutability: 'nonpayable',
            type: 'function',
        },
        {
            constant: true,
            inputs: [],
            name: 'decimals',
            outputs: [
                {
                    name: '',
                    type: 'uint8',
                },
            ],
            payable: false,
            stateMutability: 'view',
            type: 'function',
        },
        {
            constant: true,
            inputs: [
                {
                    name: '_owner',
                    type: 'address',
                },
            ],
            name: 'balanceOf',
            outputs: [
                {
                    name: 'balance',
                    type: 'uint256',
                },
            ],
            payable: false,
            stateMutability: 'view',
            type: 'function',
        },
        {
            constant: true,
            inputs: [],
            name: 'symbol',
            outputs: [
                {
                    name: '',
                    type: 'string',
                },
            ],
            payable: false,
            stateMutability: 'view',
            type: 'function',
        },
        {
            constant: false,
            inputs: [
                {
                    name: '_to',
                    type: 'address',
                },
                {
                    name: '_value',
                    type: 'uint256',
                },
            ],
            name: 'transfer',
            outputs: [
                {
                    name: '',
                    type: 'bool',
                },
            ],
            payable: false,
            stateMutability: 'nonpayable',
            type: 'function',
        },
        {
            constant: true,
            inputs: [
                {
                    name: '_owner',
                    type: 'address',
                },
                {
                    name: '_spender',
                    type: 'address',
                },
            ],
            name: 'allowance',
            outputs: [
                {
                    name: '',
                    type: 'uint256',
                },
            ],
            payable: false,
            stateMutability: 'view',
            type: 'function',
        },
        {
            payable: true,
            stateMutability: 'payable',
            type: 'fallback',
        },
        {
            anonymous: false,
            inputs: [
                {
                    indexed: true,
                    name: 'owner',
                    type: 'address',
                },
                {
                    indexed: true,
                    name: 'spender',
                    type: 'address',
                },
                {
                    indexed: false,
                    name: 'value',
                    type: 'uint256',
                },
            ],
            name: 'Approval',
            type: 'event',
        },
        {
            anonymous: false,
            inputs: [
                {
                    indexed: true,
                    name: 'from',
                    type: 'address',
                },
                {
                    indexed: true,
                    name: 'to',
                    type: 'address',
                },
                {
                    indexed: false,
                    name: 'value',
                    type: 'uint256',
                },
            ],
            name: 'Transfer',
            type: 'event',
        },
    ];

    constructor(
        private providerFactoryService: EVMProviderFactoryService,
        private http: HttpClient,
        private explorerService: EVMBlockChainExplorerFactoryService,
    )
    {
        super();
        this.loadCurrenciesData();
    }

    public async create(
        _address: string,
        _network: number,
        _abi?: InterfaceAbi,
        _nameData?: { symbol: string, name: string; },
        _explorer?: string,
        implicit: boolean = false
    ): Promise<IErc20ContractData>
    {
        const address = _address.toLowerCase();

        if (Boolean(this.data[ address ]?.[ _network ]) && !implicit)
        {
            return await this.data[ address ][ _network ].pipe(take(1)).toPromise();
        }

        this.explorer = await this.explorerService.getExplorer(_network, _explorer);

        if (!this.data[ address ]) this.data[ address ] = {};

        this.data[ address ][ _network ] = new ReplaySubject(1);

        const abi = _abi || (await this.loadContractABI(address));

        const provider = await this.providerFactoryService.getProvider(_network);

        const contract = new Contract(address, abi, provider);

        const contractData = await this.getContractData(address);

        if (!!contractData)
        {
            this.data[ address ][ _network ].next({ contract, ...contractData, abi });
            return { contract, ...contractData, abi };
        }

        try
        {
            let symbol, name;
            if (_nameData && !!_nameData?.name && !!_nameData?.symbol)
            {
                symbol = _nameData.symbol;
                name = _nameData.name;
            }
            else
            {
                [ symbol, name ] = await Promise.all([
                    contract.symbol(),
                    contract.name(),
                ]);
            }

            const contractData = { contract, symbol, name, abi };

            this.data[ address ][ _network ].next(contractData);

            return contractData;
        } catch (e)
        {
            console.error(e);
            this.data[ address ][ _network ].next({ contract });
            return { contract };
        }
    }

    public loadContractABI(_address?: string): InterfaceAbi
    {
        return this.ERC20_ABI;
    }

    private async loadCurrenciesData()
    {
        const response = <any>(
            await this.http
                .get(`/assets/data/currencies.json?t=${ Date.now() }`)
                .toPromise()
        );

        this.currenciesData.next(response);
    }

    private getContractData(
        _address
    ): Promise<{ name: string; symbol: string; }>
    {
        return this.currenciesData
            .pipe(
                map((_data) => _data[ _address ]),
                take(1)
            )
            .toPromise();
    }
}

